import { parseInstrument } from "./displayName";

/**
 * Instruments whose chart should render a specific *native* TradingView symbol
 * (perpetuals → the OKX perpetual feed; metals/FX/equities → their real tickers)
 * instead of the derived spot pair. Keys are UPPERCASE instrument names.
 *
 * This is the single source of truth for chart symbol overrides — add new
 * markets here, not inline in a component.
 */
export const tradingViewSymbolByInstrument: Record<string, string> = {
  "BTC-PERP": "OKX:BTCUSD.P",
  "BTC-USD": "OKX:BTCUSD.P",
  "ETH-PERP": "OKX:ETHUSD.P",
  "ETH-USD": "OKX:ETHUSD.P",
  "MON-PERP": "COINBASE:MONUSD",
  "MON-USD": "COINBASE:MONUSD",
  "XAU-PERP": "OANDA:XAUUSD",
  "XAU-USD": "OANDA:XAUUSD",
  "GOLD-PERP": "OANDA:XAUUSD",
  "GOLD-USD": "OANDA:XAUUSD",
  "XAG-PERP": "OANDA:XAGUSD",
  "XAG-USD": "OANDA:XAGUSD",
  "SILVER-PERP": "OANDA:XAGUSD",
  "SILVER-USD": "OANDA:XAGUSD",
  "EUR-PERP": "OANDA:EURUSD",
  "EUR-USD": "OANDA:EURUSD",
  "JPY-PERP": "OANDA:USDJPY",
  "JPY-USD": "OANDA:USDJPY",
  "SPX-PERP": "SP:SPX",
  "SPX-USD": "SP:SPX",
  "SP500-PERP": "SP:SPX",
  "SP500-USD": "SP:SPX",
  "TSLA-PERP": "NASDAQ:TSLA",
  "TSLA-USD": "NASDAQ:TSLA",
  "NVDA-PERP": "NASDAQ:NVDA",
  "NVDA-USD": "NASDAQ:NVDA",
  "AAPL-PERP": "NASDAQ:AAPL",
  "AAPL-USD": "NASDAQ:AAPL",
  "ANTHROPIC-PERP": "OKX:ANTHROPICUSDT.P",
  "ANTHROPIC-USD": "OKX:ANTHROPICUSDT.P",
};

/**
 * Resolve a raw instrument name to a symbol that TradingView's native feed can
 * load. One reliable path for every chart (spot/perp, both frontends):
 *
 *   1. Exact override — perps & non-crypto markets map to their native symbol.
 *   2. Normalized spot pair — mock-token prefixes are stripped via the shared
 *      `parseInstrument` (e.g. "MWETH-USDC" → "ETHUSDC"). The normalized
 *      "BASE-QUOTE" key is re-checked against the override map first.
 *   3. Fallback — strip separators.
 *
 * Step 2 is why spot markets backed by mock tokens (MWETH, MWBTC, MUSDC) resolve
 * instead of showing TradingView's "This symbol doesn't exist": the raw
 * "MWETHUSDC" is not a real ticker, but the normalized "ETHUSDC" is.
 */
export const getTradingViewSymbol = (symbol?: string): string => {
  const normalized = (symbol || "").trim().toUpperCase();

  if (tradingViewSymbolByInstrument[normalized]) {
    return tradingViewSymbolByInstrument[normalized];
  }

  const parsed = parseInstrument(normalized);
  if (parsed) {
    const canonical = `${parsed.base}-${parsed.quote}`.toUpperCase();
    return (
      tradingViewSymbolByInstrument[canonical] ||
      `${parsed.base}${parsed.quote}`.toUpperCase()
    );
  }

  return normalized.replace(/-/g, "");
};
